import { useCallback } from "react";
import ForceGraph2D from "react-force-graph-2d";
import { Loader2, Bot, ZoomIn, ZoomOut, Maximize2, RefreshCw } from "lucide-react";

export interface GraphNode {
	id: string;
	name: string;
	properties?: Record<string, unknown>;
	val: number;
	color?: string;
}

export interface GraphLink {
	source: string;
	target: string;
	name: string;
	rid: string;
}

interface KnowledgeGraphProps {
	graphRef: React.RefObject<any>;
	containerRef: React.RefObject<HTMLDivElement | null>;
	graphData: { nodes: GraphNode[]; links: GraphLink[] };
	graphLoading: boolean;
	dimensions: { width: number; height: number };
	selectedNode: GraphNode | null;
	onNodeClick: (node: any) => void;
	onRefresh: () => void;
}

export default function KnowledgeGraph({
	graphRef,
	containerRef,
	graphData,
	graphLoading,
	dimensions,
	selectedNode,
	onNodeClick,
	onRefresh,
}: KnowledgeGraphProps) {
	const nodeCanvasObject = useCallback(
		(node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
			const label = node.name as string;
			const fontSize = Math.max(12 / globalScale, 2);
			const isSelected = selectedNode?.id === node.id;
			const nodeRadius = isSelected ? 8 : 5;
			ctx.beginPath();
			ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI);
			ctx.fillStyle = node.color || "#3b82f6";
			ctx.fill();
			if (isSelected) {
				ctx.strokeStyle = "#ffffff";
				ctx.lineWidth = 2 / globalScale;
				ctx.stroke();
			}
			ctx.font = `${fontSize}px Sans-Serif`;
			ctx.textAlign = "center";
			ctx.textBaseline = "middle";
			ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
			ctx.fillText(label, node.x, node.y + nodeRadius + fontSize);
		},
		[selectedNode],
	);

	const nodePointerAreaPaint = useCallback(
		(node: any, color: string, ctx: CanvasRenderingContext2D) => {
			ctx.beginPath();
			ctx.arc(node.x, node.y, 8, 0, 2 * Math.PI);
			ctx.fillStyle = color;
			ctx.fill();
		},
		[],
	);

	return (
		<>
			<div ref={containerRef} className="absolute inset-0">
				{graphLoading && graphData.nodes.length === 0 ? (
					<div className="flex items-center justify-center h-full">
						<Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
					</div>
				) : graphData.nodes.length === 0 ? (
					<div className="flex flex-col items-center justify-center h-full text-center">
						<Bot className="w-16 h-16 text-slate-700 mb-4" />
						<p className="text-slate-400 text-lg font-medium">Knowledge graph is empty</p>
						<p className="text-slate-500 text-sm mt-1">Start chatting below to add knowledge</p>
					</div>
				) : (
					<ForceGraph2D
						ref={graphRef}
						graphData={graphData}
						width={dimensions.width}
						height={dimensions.height}
						nodeId="id"
						linkSource="source"
						linkTarget="target"
						nodeCanvasObject={nodeCanvasObject}
						nodePointerAreaPaint={nodePointerAreaPaint}
						linkColor={() => "rgba(100, 116, 139, 0.4)"}
						linkWidth={1.5}
						linkDirectionalArrowLength={4}
						linkDirectionalArrowRelPos={1}
						linkLabel="name"
						linkDirectionalParticles={1}
						linkDirectionalParticleWidth={2}
						onNodeClick={onNodeClick}
						onNodeDragEnd={(node: any) => {
							node.fx = node.x;
							node.fy = node.y;
						}}
						cooldownTicks={100}
						backgroundColor="transparent"
						enableNodeDrag={true}
						enableZoomInteraction={true}
						enablePanInteraction={true}
					/>
				)}
			</div>

			{/* Zoom controls */}
			<div className="absolute top-4 right-4 flex items-center gap-1.5 z-30">
				<button
					onClick={() => graphRef.current?.zoom(graphRef.current.zoom() * 1.5, 300)}
					className="p-2 bg-slate-800/80 backdrop-blur border border-slate-700/50 text-slate-400 hover:text-white rounded-lg transition-all"
					title="Zoom In"
				>
					<ZoomIn className="w-4 h-4" />
				</button>
				<button
					onClick={() => graphRef.current?.zoom(graphRef.current.zoom() / 1.5, 300)}
					className="p-2 bg-slate-800/80 backdrop-blur border border-slate-700/50 text-slate-400 hover:text-white rounded-lg transition-all"
					title="Zoom Out"
				>
					<ZoomOut className="w-4 h-4" />
				</button>
				<button
					onClick={() => graphRef.current?.zoomToFit(400, 50)}
					className="p-2 bg-slate-800/80 backdrop-blur border border-slate-700/50 text-slate-400 hover:text-white rounded-lg transition-all"
					title="Fit All"
				>
					<Maximize2 className="w-4 h-4" />
				</button>
				<button
					onClick={onRefresh}
					disabled={graphLoading}
					className="p-2 bg-slate-800/80 backdrop-blur border border-slate-700/50 text-slate-400 hover:text-white rounded-lg transition-all disabled:opacity-50"
					title="Refresh"
				>
					<RefreshCw className={`w-4 h-4 ${graphLoading ? "animate-spin" : ""}`} />
				</button>
			</div>
		</>
	);
}
